import React, { useState, useEffect } from 'react';
import { FiCalendar, FiClock, FiCheckCircle, FiXCircle, FiUsers, FiTool } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    completedBookings: 0,
    canceledBookings: 0,
    totalCustomers: 0,
    totalMechanics: 0
  });
  const [bookingsByMonth, setBookingsByMonth] = useState([]);
  const [serviceStats, setServiceStats] = useState([]);
  const [mechanicStats, setMechanicStats] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        const [statsRes, bookingsRes, serviceStatsRes, mechanicStatsRes] = await Promise.all([
          API.get('/api/dashboard/stats'),
          API.get('/api/dashboard/bookings-by-month'),
          API.get('/api/dashboard/service-stats'),
          API.get('/api/dashboard/mechanic-stats')
        ]);
        
        setStats(statsRes.data);
        setBookingsByMonth(bookingsRes.data);
        setServiceStats(serviceStatsRes.data);
        setMechanicStats(mechanicStatsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setStats({
          totalBookings: 0,
          pendingBookings: 0,
          completedBookings: 0,
          canceledBookings: 0,
          totalCustomers: 0,
          totalMechanics: 0
        });
        setBookingsByMonth([]);
        setServiceStats([]);
        setMechanicStats([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  const COLORS = ['#FF6B00', '#FF8A33', '#FFA866', '#FFC699', '#FFE4CC'];
  
  const StatCard = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-secondary mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout userRole="admin" userName="Admin">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary">Dashboard Admin</h1>
        <p className="text-gray-600">Selamat datang di panel admin XSignature Auto Garage</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard 
          title="Total Booking" 
          value={stats.totalBookings} 
          icon={<FiCalendar size={24} className="text-white" />}
          color="bg-primary"
        />
        <StatCard 
          title="Booking Menunggu" 
          value={stats.pendingBookings} 
          icon={<FiClock size={24} className="text-white" />}
          color="bg-yellow-500"
        />
        <StatCard 
          title="Booking Selesai" 
          value={stats.completedBookings} 
          icon={<FiCheckCircle size={24} className="text-white" />}
          color="bg-green-500"
        />
        <StatCard 
          title="Booking Dibatalkan" 
          value={stats.canceledBookings} 
          icon={<FiXCircle size={24} className="text-white" />}
          color="bg-red-500"
        />
        <StatCard 
          title="Total Pelanggan" 
          value={stats.totalCustomers} 
          icon={<FiUsers size={24} className="text-white" />}
          color="bg-blue-500"
        />
        <StatCard 
          title="Total Montir" 
          value={stats.totalMechanics} 
          icon={<FiTool size={24} className="text-white" />}
          color="bg-purple-500"
        />
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly Bookings Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-secondary mb-4">Booking Bulanan</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={bookingsByMonth}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="bookings" fill="#FF6B00" name="Jumlah Booking" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Service Distribution Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-secondary mb-4">Distribusi Layanan</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={serviceStats}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {serviceStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Mechanic Performance */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-lg font-semibold text-secondary mb-4">Performa Montir</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jumlah Pekerjaan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performa</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mechanicStats.map((mechanic, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{mechanic.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900">{mechanic.jobs}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900 flex items-center">
                      {mechanic.rating}
                      <div className="ml-2 text-yellow-400">★</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-primary h-2.5 rounded-full" 
                        style={{ width: `${(mechanic.rating / 5) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard; 